import { cloneDeep, sumBy } from "lodash";
import moment from "moment";

export function mathFix(value: number, dec: number = 2) {
    if (dec >= 0) {
        const r = Math.pow(10, dec);
        return Math.round(value * r) / r;
    }

    return value;
}

export function getTextFromStringArray(stringArray) {
    if (stringArray?.length) {
        if (stringArray.length === 1) {
            return stringArray[0];
        } else {
            return stringArray.join(" ");
        }
    }
    return "";
}

// // checkCorporateTax front + back
export function calculateAdjustmentTableSums(adjustmentTable) {
    // ** first line of the table
    adjustmentTable[0].manualAmountSigned = adjustmentTable[0].computedAmountSigned;
    // ** total réintégrations
    const reinstatementsFilter = adjustmentTable.filter(
        (at) => at.sumBlock === "reinstatementsSum"
    );

    const findReinstatementsSum = adjustmentTable.find(
        (at) => at.tableBlock === "reinstatementsSum"
    );

    findReinstatementsSum.manualAmountSigned = -sumBy(reinstatementsFilter, "manualAmountSigned");

    // ** total déductions
    const deductionsFilter = adjustmentTable.filter((at) => at.sumBlock === "deductionsSum");

    const findDeductionsSum = adjustmentTable.find((at) => at.tableBlock === "deductionsSum");

    findDeductionsSum.manualAmountSigned = sumBy(deductionsFilter, "manualAmountSigned");

    // ** résultat fiscal imposable
    const taxableTaxResultFilter = adjustmentTable.filter(
        (at) => at.sumBlock === "taxableTaxResult"
    );

    const findTaxableTaxResult = adjustmentTable.find((at) => at.tableBlock === "taxableTaxResult");

    findTaxableTaxResult.manualAmountSigned = sumBy(taxableTaxResultFilter, "manualAmountSigned");

    if (findTaxableTaxResult.manualAmountSigned < 0) {
        findTaxableTaxResult.wording = "BENEFICE FISCAL IMPOSABLE";
    } else if (findTaxableTaxResult.manualAmountSigned > 0) {
        findTaxableTaxResult.wording = "DEFICIT FISCAL";
    }
}

// // checkCorporateTax front + back
export function calculateTax(
    adjustmentTable,
    bundleSheetId,
    preTaxIncomeFromBundle,
    period,
    year,
    naturalPersonsSharesRateOver75,
    salesTurnover,
    customerCorporateTaxObject,
    previousCreditsAllocation,
    onAccountPayments,
    miscellanousTaxCredits,
    preTaxIncomeBrackets,
    turnoverBrackets,
    corporateTaxRates,
    fullyPaidCapital,
    corporateTaxInBundleSheet,
    balances695,
    acrResults
) {
    let preTaxIncome = bundleSheetId?.length
        ? preTaxIncomeFromBundle
        : adjustmentTable?.length
        ? adjustmentTable[31].manualAmountSigned
        : 0;
    let calculatedCorporateTaxAmount = 0;
    let periodPreTaxIncomeBracket0 = 0;
    let periodPreTaxIncomeBracket1 = 0;
    let periodTurnoverBracket0 = 0;
    let fullPeriodTurnoverBracket0 = 0;
    let periodTurnoverBracket1 = 0;
    let fullPeriodTurnoverBracket1 = 0;

    let reducedRateAmount = 0;
    let normalRateAmount = 0;
    let overrunRateAmount = 0;

    let periodMonthDuration = Math.ceil(moment(period.end).diff(period.start, "months", true));

    // ** incomes are in c/7 : "-" signed
    if (preTaxIncome >= 0) {
        // ** loss
        calculatedCorporateTaxAmount = 0;

        customerCorporateTaxObject.reduced = {
            taxBase: 0,
            taxRate: 0,
            taxAmount: 0,
        };

        customerCorporateTaxObject.normal = {
            taxBase: 0,
            taxRate: 0,
            taxAmount: 0,
        };

        customerCorporateTaxObject.overrun = {
            taxBase: 0,
            taxRate: 0,
            taxAmount: 0,
        };
    } else if (preTaxIncome < 0) {
        // ** gain
        if (periodMonthDuration != 12) {
            periodPreTaxIncomeBracket0 = preTaxIncomeBrackets[0]?.[year]
                ? mathFix((preTaxIncomeBrackets[0][year] * periodMonthDuration) / 12, 0)
                : 0;
            periodPreTaxIncomeBracket1 = preTaxIncomeBrackets[1]?.[year]
                ? mathFix((preTaxIncomeBrackets[1][year] * periodMonthDuration) / 12, 0)
                : 0;

            periodTurnoverBracket0 = turnoverBrackets[0]?.[year]
                ? mathFix((turnoverBrackets[0][year] * periodMonthDuration) / 12, 0)
                : 0;
            fullPeriodTurnoverBracket0 = turnoverBrackets[0]?.[year]
                ? mathFix(turnoverBrackets[0][year], 0)
                : 0;
            periodTurnoverBracket1 = turnoverBrackets[1]?.[year]
                ? mathFix((turnoverBrackets[1][year] * periodMonthDuration) / 12, 0)
                : 0;
            fullPeriodTurnoverBracket1 = turnoverBrackets[1]?.[year]
                ? mathFix(turnoverBrackets[1][year], 0)
                : 0;
        } else {
            // ** plafonds bénéfice
            periodPreTaxIncomeBracket0 = preTaxIncomeBrackets[0]?.[year] || 0;
            periodPreTaxIncomeBracket1 = preTaxIncomeBrackets[1]?.[year] || 0;

            // ** plafonds CA
            periodTurnoverBracket0 = turnoverBrackets[0]?.[year] || 0;
            periodTurnoverBracket1 = turnoverBrackets[1]?.[year] || 0;
        }

        if (
            fullyPaidCapital &&
            naturalPersonsSharesRateOver75 &&
            (!salesTurnover || (salesTurnover && salesTurnover > periodTurnoverBracket0)) &&
            (!periodPreTaxIncomeBracket1 ||
                (periodPreTaxIncomeBracket1 && preTaxIncome >= periodPreTaxIncomeBracket1))
        ) {
            reducedRateAmount =
                preTaxIncome >= periodPreTaxIncomeBracket0
                    ? mathFix(preTaxIncome, 0)
                    : mathFix(periodPreTaxIncomeBracket0, 0);

            if (periodPreTaxIncomeBracket1) {
                normalRateAmount =
                    preTaxIncome >= periodPreTaxIncomeBracket1
                        ? mathFix(preTaxIncome - reducedRateAmount, 0)
                        : periodPreTaxIncomeBracket1;
            } else {
                normalRateAmount = mathFix(preTaxIncome - reducedRateAmount, 0);
            }
        } else if (periodTurnoverBracket1) {
            // ** si deuxième plafond CA (2020 500M€)
            // ** au prorata de la durée en mois entiers (arrondis au sup) de la période
            if (periodPreTaxIncomeBracket1) {
                normalRateAmount =
                    preTaxIncome >= periodPreTaxIncomeBracket1
                        ? mathFix(preTaxIncome - reducedRateAmount, 0)
                        : periodPreTaxIncomeBracket1;
            } else {
                normalRateAmount = mathFix(preTaxIncome - reducedRateAmount, 0);
            }

            // ** si CA > deuxième tranche
            if (periodPreTaxIncomeBracket1) {
                overrunRateAmount =
                    preTaxIncome < periodPreTaxIncomeBracket1
                        ? mathFix(preTaxIncome - periodPreTaxIncomeBracket1, 0)
                        : 0;
            }
        } else {
            normalRateAmount = preTaxIncome;
        }

        if (reducedRateAmount) {
            customerCorporateTaxObject.reduced = {
                taxBase: mathFix(reducedRateAmount, 0),
                taxRate: corporateTaxRates[1].rate[year],
                taxAmount: mathFix((reducedRateAmount * corporateTaxRates[1].rate[year]) / 100, 0),
            };

            calculatedCorporateTaxAmount -= customerCorporateTaxObject.reduced.taxAmount;
        } else {
            customerCorporateTaxObject.reduced = {};
        }

        if (normalRateAmount) {
            customerCorporateTaxObject.normal = {
                taxBase: mathFix(normalRateAmount, 0),
                taxRate: corporateTaxRates[2].rate[year],
                taxAmount: mathFix((normalRateAmount * corporateTaxRates[2].rate[year]) / 100, 0),
            };

            calculatedCorporateTaxAmount -= customerCorporateTaxObject.normal.taxAmount;
        } else {
            customerCorporateTaxObject.normal = {};
        }

        if (overrunRateAmount && corporateTaxRates[3]?.rate?.[year]) {
            customerCorporateTaxObject.overrun = {
                taxBase: mathFix(overrunRateAmount, 0),
                taxRate: corporateTaxRates[3].rate[year],
                taxAmount: mathFix((overrunRateAmount * corporateTaxRates[3].rate[year]) / 100, 0),
            };

            calculatedCorporateTaxAmount -= customerCorporateTaxObject.overrun.taxAmount;
        } else {
            customerCorporateTaxObject.overrun = {};
        }
    }

    const corporateTaxFromBalances695 = balances695?.length
        ? mathFix(sumBy(balances695, "amountSigned"), 0)
        : 0;

    let bundleSheetToRegenerate = false;
    const diff = mathFix(corporateTaxFromBalances695 - corporateTaxInBundleSheet, 2);
    // ** corporateTaxInBundleSheet n'est pas à jour
    if (diff) {
        bundleSheetToRegenerate = true;
    }

    const accountableAccounts695 = balances695?.map((b) => b.name);

    let onlyAcrResultsFromBalance695 = 0;
    if (acrResults?.length) {
        for (const result of acrResults) {
            const al695 = result.accountingLines.filter((al) =>
                al.accountableAccount.name.startsWith("695")
            );
            if (al695?.length) {
                onlyAcrResultsFromBalance695 += sumBy(al695, "amountSigned");
            }
        }

        if (onlyAcrResultsFromBalance695) {
            onlyAcrResultsFromBalance695 = mathFix(onlyAcrResultsFromBalance695, 0);
        }
    }

    let toBeBooked = 0;
    let previousDocumentsIds = undefined;

    // ** corporateTaxInBundleSheet may not be up to date : replaced by corporateTaxFromBalances695
    let bookedCorporateTaxAmount = corporateTaxInBundleSheet || corporateTaxFromBalances695 || 0;

    let c695gap = calculatedCorporateTaxAmount - bookedCorporateTaxAmount;

    let onlyManualOdsFromBalances695 = bookedCorporateTaxAmount - onlyAcrResultsFromBalance695;

    if (c695gap) {
        toBeBooked = calculatedCorporateTaxAmount - onlyManualOdsFromBalances695;
        if (toBeBooked && acrResults?.length) {
            previousDocumentsIds = acrResults.map((ar) => ar.id);
        }
    }

    let toBePaid =
        calculatedCorporateTaxAmount -
        previousCreditsAllocation -
        onAccountPayments -
        miscellanousTaxCredits;

    return {
        bundleSheetToRegenerate: bundleSheetToRegenerate,
        preTaxIncome: preTaxIncome,
        corporateTaxFromBalances695: corporateTaxFromBalances695, // ** must be equal to corporateTaxInBundleSheet
        corporateTaxInBundleSheet: corporateTaxInBundleSheet, // ** must be equal to corporateTaxFromBalances695
        corporateTaxAmount: calculatedCorporateTaxAmount,
        customerCorporateTaxObject: customerCorporateTaxObject,
        c695gap: c695gap,
        toBePaid: toBePaid,
        toBeBooked: toBeBooked,
        onlyManualOdsFromBalances695: onlyManualOdsFromBalances695,
        onlyAcrResultsFromBalance695: onlyAcrResultsFromBalance695,
        periodPreTaxIncomeBracket0: periodPreTaxIncomeBracket0,
        periodPreTaxIncomeBracket1: periodPreTaxIncomeBracket1,
        periodTurnoverBracket0: periodTurnoverBracket0,
        fullPeriodTurnoverBracket0: fullPeriodTurnoverBracket0,
        periodTurnoverBracket1: periodTurnoverBracket1,
        fullPeriodTurnoverBracket1: fullPeriodTurnoverBracket1,
        periodMonthDuration: periodMonthDuration,
        accountableAccount695: accountableAccounts695?.length
            ? accountableAccounts695[0]
            : "695000",
        previousDocumentsIds: previousDocumentsIds,
    };
}

// // checkClosureVAT front + back
export function getSingleAcrAmountsVAT(declaration, acrAmountsVAT) {
    const acrAmounts = acrAmountsVAT?.filter((al) => al.month === declaration.month);
    if (acrAmounts?.length) {
        for (const manualAmount of acrAmounts) {
            if (declaration.expandable && declaration.tables?.length) {
                const manualLine = declaration.tables.find((d) => d.VATtextfield === true);
                if (manualLine) {
                    manualLine[manualAmount.key] = manualAmount.amount;
                }
            } else {
                declaration[manualAmount.key] = manualAmount.amount;
            }
        }
    }
    return declaration;
}

// // checkClosureVAT front + back
export function getSingleAcrAmountsCorrections(
    generalLedgerText,
    line,
    acrAmountsCorrections,
    item1Title = undefined
) {
    let acrAmounts = acrAmountsCorrections?.filter(
        (aac) =>
            aac.title === line.title &&
            aac.generalLedgerText === generalLedgerText &&
            aac.item1Title === item1Title
    );
    if (item1Title && acrAmounts?.length) {
        acrAmounts = acrAmounts.filter((aac) => aac.item1Title === item1Title);
    }

    if (acrAmounts?.length) {
        for (const manualAmount of acrAmounts) {
            if (mathFix(line[manualAmount.key], 2) !== mathFix(manualAmount.amount, 2)) {
                line[manualAmount.key] = manualAmount.amount;
            }
        }
    }
    return line;
}

// // checkClosureVAT front + back
export function getAllAcrAmountsVAT(gapsResumeTable1Table, acrAmountsVAT) {
    if (gapsResumeTable1Table?.length && acrAmountsVAT?.length) {
        for (const vatCorrection of acrAmountsVAT) {
            const index = gapsResumeTable1Table.findIndex((i) => i.month === vatCorrection.month);

            if (index > -1) {
                gapsResumeTable1Table[index][vatCorrection.key] = vatCorrection.amount;
                gapsResumeTable1Table[index].style = "color: red";
            }
        }
    }
    return gapsResumeTable1Table;
}

// // checkClosureVAT front + back
export function getAllAcrAmountsCorrections(gapsResumeTable0Tables, acrAmountsCorrections) {
    if (gapsResumeTable0Tables?.length && acrAmountsCorrections?.length) {
        for (const correction of acrAmountsCorrections) {
            const index = gapsResumeTable0Tables.findIndex(
                (i) =>
                    i.title === correction.title &&
                    i.generalLedgerText === correction.generalLedgerText
            );

            if (index > -1) {
                gapsResumeTable0Tables[index][correction.key] = correction.amount;
                gapsResumeTable0Tables[index].style = "color: red";
            }
        }
    }
    return gapsResumeTable0Tables;
}

// // checkClosureVAT front + back
export function modifyManualAmount(
    acrAmounts,
    item,
    key,
    type,
    generalLedgerText,
    gapsResumeTable: Array<any> = [],
    item1Title: string = ""
) {
    if (item && key && type && generalLedgerText) {
        let modifiedAmounts = 0;

        if (type === "corrections") {
            if (acrAmounts.corrections?.length) {
                const alreadyManualIndex = acrAmounts.corrections.findIndex(
                    (manual) =>
                        manual.generalLedgerText === generalLedgerText &&
                        manual.title === item.title &&
                        manual.key === key &&
                        (!item1Title.length || manual.item1Title === item1Title)
                );

                if (alreadyManualIndex > -1) {
                    if (acrAmounts.corrections[alreadyManualIndex].amount !== item[key]) {
                        acrAmounts.corrections[alreadyManualIndex].amount = item[key];
                        modifiedAmounts++;
                    }
                    if (!acrAmounts.corrections[alreadyManualIndex].amount) {
                        acrAmounts.corrections.splice(alreadyManualIndex, 1);
                    }
                } else {
                    modifiedAmounts++;
                    acrAmounts.corrections.push({
                        generalLedgerText: generalLedgerText,
                        title: item.title,
                        key: key,
                        amount: mathFix(item[key], 2),
                        item1Title: item1Title,
                    });
                }
            } else {
                modifiedAmounts++;
                acrAmounts.corrections = [
                    {
                        generalLedgerText: generalLedgerText,
                        title: item.title,
                        key: key,
                        amount: mathFix(item[key], 2),
                        item1Title: item1Title,
                    },
                ];
            }
        } else if (type === "VAT") {
            if (acrAmounts.VAT?.length) {
                const alreadyManualIndex = acrAmounts.VAT.findIndex(
                    (manual) => manual.month === item1Title && manual.key === key
                );

                if (alreadyManualIndex > -1) {
                    if (acrAmounts.VAT[alreadyManualIndex].amount !== item[key]) {
                        modifiedAmounts++;
                    }

                    acrAmounts.VAT[alreadyManualIndex] = {
                        month: item1Title,
                        key: key,
                        amount: mathFix(item[key], 2),
                    };
                } else {
                    modifiedAmounts++;
                    acrAmounts.VAT.push({
                        month: item1Title,
                        key: key,
                        amount: mathFix(item[key], 2),
                    });
                }
            } else {
                modifiedAmounts++;
                acrAmounts.VAT = [
                    {
                        month: item1Title,
                        key: key,
                        amount: mathFix(item[key], 2),
                    },
                ];
            }
        }
        if (modifiedAmounts) {
            const xx = modifySums(gapsResumeTable);
            return {
                gapsResumeTable: xx,
                acrAmounts: acrAmounts,
            };
        }
    } else {
        return {
            gapsResumeTable: gapsResumeTable,
            acrAmounts: acrAmounts,
        };
    }
}

// // checkClosureVAT front + back
export function modifySums(gapsResumeTable) {
    if (gapsResumeTable.length) {
        // ** after manual input of HT30 HT50 HT80 or HT90
        // ** this.columnsToShow = used ones between ["HT20","VAT20","HT10","VAT10","HT08","VAT08","HT05","VAT05","HT02","VAT02","HT00","HT30","HT50","HT80","HT90"]
        // ** without TTCsum and HTsum

        const columnsHT = Object.keys(gapsResumeTable[1].table[0]).filter(
            (key) => key.startsWith("HT") && key != "HTsum"
        );
        const columnsVAT = Object.keys(gapsResumeTable[1].table[0]).filter(
            (key) => key.startsWith("VAT") && key != "VATsum"
        );
        const columnsHT_VAT = [...columnsHT, ...columnsVAT];
        const columnsHtTvaWithSums = Object.keys(gapsResumeTable[1].table[0]).filter(
            (key) => key.startsWith("HT") || key.startsWith("VAT")
        );

        // ++ gapsResumeTable[0] = C.A.
        // -- array are less clear than object
        // -- but are needed in front to have nested tables
        const adjustmentTables = [];

        // ** gapsResumeTable[0] = C.A.
        for (let item1 of gapsResumeTable[0].tables) {
            // ** gapsResumeTable[0].tables?.[0] = "+ Chiffre d'affaires comptable de l'exercice N"
            // ** gapsResumeTable[0].tables?.[1] = "+ Régularisations de début d'exercice" = N - 1
            // ** gapsResumeTable[0].tables?.[2] = "- Régularisations de fin d'exercice" = N
            // ** gapsResumeTable[0].tables?.[3] = "+ Régularisations exceptionnelles de fin d'exercice" = N
            if (item1.expandable && item1?.table?.length) {
                // ** gapsResumeTable[0].tables?.[1]?.table?.[0] = "411000 - Clients - TOTAL"
                for (let item2 of item1?.table) {
                    if (item2.expandable && item2.table?.length) {
                        for (let item3 of item2.table) {
                            if (item3.HTtextfield) {
                                item3 = calculateAdjustmentSumsFromHT(item3, columnsHT);
                                adjustmentTables.push(item3);
                            }
                        }
                    } else if (item2.HTtextfield) {
                        item2 = calculateAdjustmentSumsFromHT(item2, columnsHT);
                        adjustmentTables.push(item2);
                    }
                }
            } else if (item1.HTtextfield) {
                item1 = calculateAdjustmentSumsFromHT(item1, columnsHT);
                adjustmentTables.push(item1);
            }
        }

        // ** gapsResumeTable[0] = C.A.
        for (const item1 of gapsResumeTable[0].tables) {
            // ** gapsResumeTable[0].tables?.[0] = "+ Chiffre d'affaires comptable de l'exercice N"
            // ** gapsResumeTable[0].tables?.[1] = "+ Régularisations de début d'exercice" = N - 1
            // ** gapsResumeTable[0].tables?.[2] = "- Régularisations de fin d'exercice" = N
            // ** gapsResumeTable[0].tables?.[3] = "+ Régularisations exceptionnelles de fin d'exercice" = N
            if (item1?.table?.length) {
                // ** gapsResumeTable[0].tables?.[1]?.table?.[0] = "411000 - Clients - TOTAL"
                for (const item2 of item1?.table) {
                    if (item2.table?.length) {
                        for (const key of columnsHtTvaWithSums) {
                            item2[key] = mathFix(sumBy(item2.table, key), 2);
                        }
                    }
                }
                for (const key of columnsHtTvaWithSums) {
                    item1[key] = mathFix(sumBy(item1.table, key), 2);
                }
            }
        }
        for (const key of columnsHtTvaWithSums) {
            gapsResumeTable[0][key] = mathFix(sumBy(gapsResumeTable[0].tables, key), 2);
        }

        // ++ gapsResumeTable[1] = VAT déclarations
        // **  gapsResumeTable[1].table[0] = declaration 01/N
        for (let declaration of gapsResumeTable[1].table) {
            declaration.HTsum = 0;
            declaration.VATsum = 0;

            if (declaration.expandable && declaration?.tables?.length) {
                for (let declaration2 of declaration.tables) {
                    declaration2.HTsum = 0;
                    declaration2.VATsum = 0;
                    if (declaration2.VATtextfield) {
                        declaration2 = calculateAdjustmentSumsFromVAT(declaration2, columnsVAT);
                        adjustmentTables.push(declaration2);
                    }
                }
                for (const key of columnsHT_VAT) {
                    declaration[key] = mathFix(sumBy(declaration.tables, key), 2);
                }
            } else if (declaration.VATtextfield) {
                declaration = calculateAdjustmentSumsFromVAT(declaration, columnsVAT);
                adjustmentTables.push(declaration);
            }

            for (const key of columnsHT) {
                declaration.HTsum += declaration[key];
            }
            declaration.HTsum = mathFix(declaration.HTsum, 2);

            for (const key of columnsVAT) {
                declaration.VATsum += declaration[key];
            }
            declaration.VATsum = mathFix(declaration.VATsum, 2);
        }

        // ** new sums in gapsResumeTable[1] VAT
        // ** only inner columns
        for (const key of columnsHtTvaWithSums) {
            gapsResumeTable[1][key] = mathFix(sumBy(gapsResumeTable[1].table, key), 2);
        }

        // ++ gapsResumeTable[2] = gaps
        // ** new gaps = this.gapsResumeTable[2]
        for (const key of columnsHtTvaWithSums) {
            gapsResumeTable[2][key] = mathFix(gapsResumeTable[0][key] - gapsResumeTable[1][key], 2);
        }
    }
    return gapsResumeTable;
}

// // checkClosureVAT front + back
export function modifyColumns(
    acrAmounts,
    gapsResumeTable,
    persistentGapsResumeTableHeaders,
    columnsToShowFromDocuments,
    columnsToShow,
    optionnalsGapsResumeTableHeaders,
    selectedOptionnalsColumns,
    gapsResumeTableFilteredHeaders
) {
    const allManualKeys = [];
    let keysNotInColumnsToShowFromDocuments = [];
    let uniquesManualKeys = [];

    if (
        (acrAmounts?.VAT || acrAmounts?.corrections) &&
        gapsResumeTable?.length &&
        columnsToShow?.length &&
        optionnalsGapsResumeTableHeaders?.length &&
        gapsResumeTableFilteredHeaders?.length
    ) {
        if (acrAmounts && (acrAmounts.corrections?.length || acrAmounts.VAT?.length)) {
            if (acrAmounts.corrections?.length) {
                allManualKeys.push(acrAmounts.corrections.map((i) => i.key));
            }

            if (acrAmounts.VAT?.length) {
                allManualKeys.push(acrAmounts.VAT.map((i) => i.key));

                for (const item of acrAmounts.VAT) {
                    const modifiedMonth = gapsResumeTable[1].table.find(
                        (month) => month.month === item.month
                    );
                    modifiedMonth[item.key] = item.amount;
                    modifiedMonth.style = "color: red";
                }
            }
        }
    }

    if (selectedOptionnalsColumns?.length) {
        allManualKeys.push(selectedOptionnalsColumns.map((i) => i.value));
    }

    uniquesManualKeys = [...new Set(allManualKeys)].flat();

    keysNotInColumnsToShowFromDocuments = uniquesManualKeys?.length
        ? uniquesManualKeys.filter((k) => !columnsToShowFromDocuments.includes(k))
        : [];

    const onlymanualsColumnsHeaders = keysNotInColumnsToShowFromDocuments.length
        ? optionnalsGapsResumeTableHeaders.filter((h) =>
              keysNotInColumnsToShowFromDocuments.includes(h.value)
          )
        : [];

    gapsResumeTableFilteredHeaders = [
        ...persistentGapsResumeTableHeaders,
        ...optionnalsGapsResumeTableHeaders.filter((h) =>
            columnsToShowFromDocuments.includes(h.value)
        ),
        ...onlymanualsColumnsHeaders,
    ];

    columnsToShow = [...columnsToShowFromDocuments, ...keysNotInColumnsToShowFromDocuments];

    return {
        gapsResumeTable: gapsResumeTable,
        gapsResumeTableFilteredHeaders: gapsResumeTableFilteredHeaders,
        columnsToShow: columnsToShow,
    };
}

// // CheckFixedAssetsCutPrevious front + back
export const decliningBalanceFactor = [
    {
        minYearDuration: 3,
        maxYearDuration: 4,
        factor: 1.25,
        // rateDeclining: {
        //     3: {
        //         rate: 100 / 3,
        //         rateDeclining: 100/3*1.25
        //     },
        //     4: {
        //         rate: 100 / 4,
        //         rateDeclining: 100/4*1.25
        //     }
        // }
    },
    {
        minYearDuration: 5,
        maxYearDuration: 6,
        factor: 1.75,
        // rateDeclining: {
        //     5: {
        //         rate: 100 / 5,
        //         rateDeclining: 100/5*1.25
        //     },
        //     6: {
        //         rate: 100 / 6,
        //         rateDeclining: 100/6*1.25
        //     }
        // }
    },
    {
        minYearDuration: 7,
        maxYearDuration: null,
        factor: 2.25,
    },
];

// // Disabled audit keys front + back
export const disabledAuditKeys = ["xhtWyyZWjARdHDxNMtWAmUwMV", "k0WcnJvR7bjs1cDvpQ1qjpHnP"];

// ** needed in front ToDepreciate + back getDeclarationData
export function getPeriodLine(alAcrData, accountingPeriod) {
    const depreciationTable = cloneDeep(alAcrData.depreciationTable);
    let periodLineData: any = {};

    if (depreciationTable?.length) {
        let periodLine = depreciationTable.find(
            (dt) => dt.resumePeriodEnd === moment(accountingPeriod.end).format("YYYY-MM-DD")
        );

        if (!periodLine) {
            if (
                depreciationTable[0].resumePeriodEnd >
                moment(accountingPeriod.end).format("YYYY-MM-DD")
            ) {
                periodLineData = {
                    previousDepreciations: 0,
                    currentLinearDepreciation: 0, // ** no currentLinearDepreciation
                    currentDerogationDepreciation: 0, // ** no currentDerogationDepreciation
                    accumulatedDepreciations: 0,
                    netBookValueEnd: alAcrData.alNetAmountSigned || 0,
                };
            } else {
                periodLine = depreciationTable[depreciationTable.length - 1];

                periodLineData = {
                    previousDepreciations:
                        periodLine.decliningAccumulatedDepreciation ||
                        periodLine.linearAccumulatedDepreciation ||
                        0,
                    currentLinearDepreciation: 0, // ** no currentLinearDepreciation
                    currentDerogationDepreciation: 0, // ** no currentDerogationDepreciation
                    accumulatedDepreciations:
                        periodLine.decliningAccumulatedDepreciation ||
                        periodLine.linearAccumulatedDepreciation ||
                        0,
                    netBookValueEnd:
                        periodLine.decliningNetBookValue3112 ||
                        periodLine.linearNetBookValue3112 ||
                        0,
                };
            }
        } else {
            periodLineData = {
                previousDepreciations: periodLine.decliningAccumulatedDepreciationStart
                    ? mathFix(periodLine.decliningAccumulatedDepreciationStart, 2)
                    : periodLine.linearAccumulatedDepreciationStart
                    ? mathFix(periodLine.linearAccumulatedDepreciationStart, 2)
                    : 0,
                currentLinearDepreciation: periodLine.linearYearDepreciation || 0,
                currentDerogationDepreciation: periodLine.derogationYearDepreciation || 0,
                accumulatedDepreciations: periodLine.decliningAccumulatedDepreciation
                    ? mathFix(periodLine.decliningAccumulatedDepreciation, 2)
                    : periodLine.linearAccumulatedDepreciation
                    ? mathFix(periodLine.linearAccumulatedDepreciation, 2)
                    : 0,
                netBookValueEnd: periodLine.decliningNetBookValue3112
                    ? mathFix(periodLine.decliningNetBookValue3112, 2)
                    : periodLine.linearNetBookValue3112
                    ? mathFix(periodLine.linearNetBookValue3112, 2)
                    : 0,
            };
        }
    } else {
        // ** no depreciationTable
        periodLineData = {
            previousDepreciations: 0,
            currentLinearDepreciation: 0, // ** no currentLinearDepreciation
            currentDerogationDepreciation: 0, // ** no currentDerogationDepreciation
            accumulatedDepreciations: 0,
            netBookValueEnd: alAcrData.alNetAmountSigned || 0,
        };
    }
    return periodLineData;
}

function calculateAdjustmentSumsFromHT(adjustment, columnsHT) {
    adjustment.HTsum = 0;
    adjustment.VATsum = 0;

    for (const key of columnsHT) {
        const last2 = key.slice(-2);

        switch (last2) {
            case "02":
                const vatAmountSigned2 = mathFix((adjustment[key] * 2.1) / 100, 2);
                adjustment.VAT02 = vatAmountSigned2;
                adjustment.VATsum += vatAmountSigned2;
                adjustment.HTsum += adjustment[key];
                break;
            case "05":
                const vatAmountSigned5 = mathFix((adjustment[key] * 5.5) / 100, 2);
                adjustment.VAT05 = vatAmountSigned5;
                adjustment.VATsum += vatAmountSigned5;
                adjustment.HTsum += adjustment[key];
                break;
            case "08": // TAUX TVA 8.5
                const vatAmountSigned8 = mathFix((adjustment[key] * 8.5) / 100, 2);
                adjustment.VAT08 = vatAmountSigned8;
                adjustment.VATsum += vatAmountSigned8;
                adjustment.HTsum += adjustment[key];
                break;
            case "00":
                adjustment.HTsum += adjustment[key];
                break;
            case "10":
                const vatAmountSigned10 = mathFix((adjustment[key] * 10) / 100, 2);
                adjustment.VAT10 = vatAmountSigned10;
                adjustment.VATsum += vatAmountSigned10;
                adjustment.HTsum += adjustment[key];
                break;
            case "20":
                const vatAmountSigned20 = mathFix((adjustment[key] * 20) / 100, 2);
                adjustment.VAT20 = vatAmountSigned20;
                adjustment.VATsum += vatAmountSigned20;
                adjustment.HTsum += adjustment[key];
                break;
            case "30":
                adjustment.HTsum += adjustment[key];
                break;
            case "80":
                adjustment.HTsum += adjustment[key];
                break;
            case "90":
                adjustment.HTsum += adjustment[key];
                break;
            case "50":
                adjustment.HTsum += adjustment[key];
                break;
            default:
                debugger;
        }
    }
    return adjustment;
}

function calculateAdjustmentSumsFromVAT(adjustment, columnsVAT) {
    adjustment.HTsum = 0;
    adjustment.VATsum = 0;

    for (const key of columnsVAT) {
        const last2 = key.slice(-2);

        switch (last2) {
            case "02":
                const htAmountSigned2 = mathFix((adjustment[key] * 100) / 2.1, 2);
                adjustment.HT02 = htAmountSigned2;
                adjustment.HTsum += htAmountSigned2;
                adjustment.VATsum += adjustment[key];
                break;
            case "05":
                const htAmountSigned5 = mathFix((adjustment[key] * 100) / 5.5, 2);
                adjustment.HT05 = htAmountSigned5;
                adjustment.HTsum += htAmountSigned5;
                adjustment.VATsum += adjustment[key];
                break;
            case "08": // TAUX TVA 8.5
                const htAmountSigned8 = mathFix((adjustment[key] * 100) / 8.5, 2);
                adjustment.HT08 = htAmountSigned8;
                adjustment.HTsum += htAmountSigned8;
                adjustment.VATsum += adjustment[key];
                break;
            case "10":
                const htAmountSigned10 = mathFix((adjustment[key] * 100) / 10, 2);
                adjustment.HT10 = htAmountSigned10;
                adjustment.HTsum += htAmountSigned10;
                adjustment.VATsum += adjustment[key];
                break;
            case "20":
                const htAmountSigned20 = mathFix((adjustment[key] * 100) / 20, 2);
                adjustment.HT20 = htAmountSigned20;
                adjustment.HTsum += htAmountSigned20;
                adjustment.VATsum += adjustment[key];
                break;
            default:
                debugger;
        }
    }
    return adjustment;
}
